/**
 * ProductDataTab – shows the customer's saved products with Excel download.
 * Used inside AccountPage.js
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileDown, Package, ExternalLink, Calendar, RefreshCw,
  Download, AlertCircle, BadgeCheck, Crown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function UsageBar({ used, limit }) {
  const unlimited = limit === -1;
  const pct = unlimited ? 100 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const remaining = unlimited ? "∞" : Math.max(0, limit - used);
  return (
    <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 flex items-center gap-4">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-bold text-slate-600">Downloads Used</span>
          <span className="text-xs font-semibold text-orange-600">
            {used} / {unlimited ? "Unlimited" : limit}
          </span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-2 rounded-full transition-all ${pct >= 90 ? "bg-red-500" : "bg-orange-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <div className="text-center min-w-[60px]">
        <p className="text-lg font-black text-orange-600">{remaining}</p>
        <p className="text-[10px] text-slate-400">remaining</p>
      </div>
    </div>
  );
}

export default function ProductDataTab({ token, onNeedPlan }) {
  const [items, setItems]     = useState([]);
  const [subInfo, setSubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, subRes] = await Promise.all([
        axios.get(`${API}/customer/product-data`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customer/subscription`,  { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setItems(itemsRes.data);
      setSubInfo(subRes.data);
    } catch (e) {
      toast.error("Failed to load product data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const downloadExcel = async (productId, title) => {
    setDownloading(productId);
    try {
      const res = await axios.get(`${API}/customer/product-data/download/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `IMPORTREND_${productId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${title}`);
      load(); // refresh download count
    } catch (e) {
      toast.error("Download failed. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  const used  = subInfo?.downloads_used  ?? 0;
  const limit = subInfo?.downloads_limit ?? 0;
  const hasPlan = subInfo?.has_plan;
  const planName = hasPlan
    ? (subInfo?.subscription?.plan_name || "Active Plan")
    : (subInfo?.free_plan?.name || "Free Plan");

  return (
    <div className="space-y-4" data-testid="product-data-tab">
      {/* Usage bar */}
      {subInfo && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {hasPlan
                ? <Crown className="w-4 h-4 text-orange-500" />
                : <BadgeCheck className="w-4 h-4 text-slate-400" />}
              <span className="text-sm font-semibold text-slate-700">{planName}</span>
              {hasPlan && subInfo.subscription?.end_date && (
                <span className="text-xs text-slate-400">
                  · Expires {fmtDate(subInfo.subscription.end_date)}
                </span>
              )}
            </div>
            {!hasPlan && (
              <button
                data-testid="upgrade-plan-btn"
                onClick={onNeedPlan}
                className="text-xs font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                Upgrade Plan
              </button>
            )}
          </div>
          <UsageBar used={used} limit={limit} />
        </div>
      )}

      {/* Table */}
      {items.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl flex flex-col items-center justify-center py-16 gap-3">
          <Package className="w-10 h-10 text-slate-300" />
          <p className="text-sm font-semibold text-slate-500">No product data yet</p>
          <p className="text-xs text-slate-400 text-center max-w-xs">
            Click "Get Product Data" on any product page to add it here and download the full Excel export.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <span className="col-span-1 text-[11px] font-bold text-slate-400 uppercase">#</span>
            <span className="col-span-4 text-[11px] font-bold text-slate-400 uppercase">Product</span>
            <span className="col-span-2 text-[11px] font-bold text-slate-400 uppercase">Product ID / SKU</span>
            <span className="col-span-3 text-[11px] font-bold text-slate-400 uppercase">Added on</span>
            <span className="col-span-1 text-[11px] font-bold text-slate-400 uppercase text-center">Downloads</span>
            <span className="col-span-1 text-[11px] font-bold text-slate-400 uppercase text-center">Export</span>
          </div>
          {items.map((item, idx) => (
            <div key={item.id}
              className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-slate-50 hover:bg-orange-50/30 transition-colors">
              <span className="col-span-1 text-xs text-slate-400 font-semibold">{idx + 1}</span>
              <div className="col-span-4 flex items-center gap-3">
                {item.product_image ? (
                  <img src={item.product_image} alt="" className="w-10 h-10 object-cover rounded-lg border border-slate-100 flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Package className="w-4 h-4 text-slate-300" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 line-clamp-2 leading-tight">{item.product_title || "—"}</p>
                  {item.product_url && (
                    <a href={item.product_url} target="_blank" rel="noreferrer"
                      className="text-[11px] text-orange-500 hover:underline flex items-center gap-0.5 mt-0.5">
                      <ExternalLink className="w-2.5 h-2.5" /> View product
                    </a>
                  )}
                </div>
              </div>
              <div className="col-span-2">
                <p className="text-xs font-mono text-slate-600 break-all">{item.product_sku || item.product_id || "—"}</p>
              </div>
              <div className="col-span-3 flex items-center gap-1.5 text-xs text-slate-500">
                <Calendar className="w-3 h-3 text-slate-300" />
                {fmtDate(item.first_added_at)}
              </div>
              <div className="col-span-1 text-center">
                <span className="text-xs font-bold text-slate-600">{item.download_count || 0}</span>
              </div>
              <div className="col-span-1 flex justify-center">
                <button
                  data-testid={`download-excel-${item.product_id}`}
                  onClick={() => downloadExcel(item.product_id, item.product_title)}
                  disabled={downloading === item.product_id}
                  title="Download Excel"
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-50 hover:bg-green-100 border border-green-200 text-green-600 transition-colors disabled:opacity-50"
                >
                  {downloading === item.product_id
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <FileDown className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
